import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Dropdown,
  Checkbox,
  Input,
} from "antd";
import React, { useCallback, useMemo, useState } from "react";
import {
  PrinterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import XLSX from "xlsx-js-style";
import {
  HEADERS_EXPORT_BEN,
  HEADERS_EXPORT_DETAIL,
  POST_LOAN_DB2_V2,
  POST_REPORT_DEBIT_BALANCE,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";

const Main = () => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const [signOut] = TokenCheck();
  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [isFlagAgreement, setIsFlagAgreement] = useState(true);
  const [isFlagEnforce, setIsFlagEnforce] = useState(true);
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [searchText, setSearchText] = useState("");

  // ── helper คำนวณยอดแต่ละ column (ใช้ร่วมกันทั้งตาราง, Excel, PDF) ──
  const getRowAmounts = (r) => {
    const ton = parseFloat(r.TON) || 0;
    const arthr = parseFloat(r.ARTHR) || 0;
    const totUpay = parseFloat(r.TOT_UPAY) || 0;
    const tonBalance = parseFloat(r.TONBALANCE) || 0;
    const creditDebtor = parseFloat(r.creditDebtor) || 0;
    const smPay = parseFloat(r.SMPAY) || 0;
    const lackBalance = parseFloat(r.LACK_BALANCE) || 0;
    const totalJugde = parseFloat(r.totalJugde) || 0;

    const yodPhonChamra = totalJugde; // ยอดผ่อนชำระ
    const yodKangwad = r.flagLoanNew === 1 ? totUpay : totalJugde; // ยอดค่างวด
    const yodChamraLaew = smPay; // ยอดชำระแล้ว
    const kangChamra =
      r.flagLoanNew === 1
        ? tonBalance
        : tonBalance > ton + arthr + lackBalance
          ? tonBalance - (ton + arthr + lackBalance)
          : 0; // ค้างชำระ
    const lukniKongluea =
      r.flagLoanNew === 1
        ? tonBalance
        : tonBalance > ton + arthr
          ? creditDebtor - (ton + arthr)
          : 0; // ลูกหนี้คงเหลือ

    return {
      yodPhonChamra,
      yodKangwad,
      yodChamraLaew,
      kangChamra,
      lukniKongluea,
    };
  };

  const calcSummary = useCallback(
    (data) =>
      data.reduce(
        (acc, r) => {
          const v = getRowAmounts(r);
          return {
            yodPhonChamra: acc.yodPhonChamra + v.yodPhonChamra,
            yodKangwad: acc.yodKangwad + v.yodKangwad,
            yodChamraLaew: acc.yodChamraLaew + v.yodChamraLaew,
            kangChamra: acc.kangChamra + v.kangChamra,
            lukniKongluea: acc.lukniKongluea + v.lukniKongluea,
          };
        },
        {
          yodPhonChamra: 0,
          yodKangwad: 0,
          yodChamraLaew: 0,
          kangChamra: 0,
          lukniKongluea: 0,
        },
      ),
    [],
  );

  // ── Query ─────────────────────────────────────────────
  const onQuery = () => {
    if (!startDate || !endDate) {
      message.warning("กรุณาเลือกวันที่ก่อนค้นหา");
      return;
    }
    queryData({
      date1: dayjs(startDate).format("YYYY-MM-DDTHH:mm:ssZ"),
      date2: dayjs(endDate).format("YYYY-MM-DDTHH:mm:ssZ"),
    });
  };

  const queryData = async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(POST_REPORT_DEBIT_BALANCE, payload, {
        headers: HEADERS_EXPORT_DETAIL,
      });
      if (res.status !== 200) {
        setArrayPay([]);
        return;
      }

      const reportData = res.data?.data ?? [];
      if (!reportData.length) {
        setArrayPay([]);
        return;
      }

      const groupedBySchema = reportData.reduce((acc, item) => {
        if (!item.CONTNO || !item.SCHEMA) return acc;
        if (!acc[item.SCHEMA]) acc[item.SCHEMA] = [];
        acc[item.SCHEMA].push(`'${item.CONTNO}'`);
        return acc;
      }, {});

      const schemaPayload = Object.entries(groupedBySchema).reduce(
        (acc, [schema, arr]) => {
          acc[schema] = arr.join(",");
          return acc;
        },
        {},
      );

      let db2Map = {};
      try {
        const db2Res = await axios.post(POST_LOAN_DB2_V2, schemaPayload, {
          headers: HEADERS_EXPORT_BEN,
        });
        const db2Data = Array.isArray(db2Res.data)
          ? db2Res.data
          : Array.isArray(db2Res.data?.data)
            ? db2Res.data.data
            : [];

        db2Data.forEach((item) => {
          const contno = item?.LOAN?.CONTNO;
          if (!contno) return;
          db2Map[contno] = {
            LOCAT: item.LOAN?.LOCAT,
            GCODE: item.MORTGAGE?.GCODE,
            PRODUCTLOAN: item.LOAN?.PRODUCTLOAN,
            ISSUNO: item.LOAN?.ISSUNO,
            TYPE: item.MORTGAGE?.TYPE,
            MODEL: item.MORTGAGE?.MODEL,
            BAAB: item.MORTGAGE?.BAAB,
            COLOR: item.MORTGAGE?.COLOR,
            REGNO: item.MORTGAGE?.REGNO,
            ENGNO: item.MORTGAGE?.ENGNO,
            MANUYR: item.MORTGAGE?.MANUYR,
            DORECV: item.MORTGAGE?.DORECV,
            OFFIC: item.CUSTOMER?.OFFIC,
            OCCUP: item.CUSTOMER?.OCCUP,
            ADDRESS: item.CUSTOMER?.ADDRESS ?? [],
            GUARANTORS: item.GUARANTORS ?? [],
          };
        });
      } catch (e) {
        console.warn("DB2 failed:", e);
      }

      setArrayPay(
        reportData
          .map((item) => ({ ...item, ...(db2Map[item.CONTNO] ?? {}) }))
          .sort((a, b) => dayjs(a.SDATE).valueOf() - dayjs(b.SDATE).valueOf()),
      );
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) signOut();
      setArrayPay([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterData = useCallback(() => {
    if (!arrayPay.length) return [];

    const keyword = searchText.trim().toLowerCase();

    return arrayPay.filter((item) => {
      // ── flag filter (เดิม) ──
      let passFlag = false;
      if (isFlagAgreement && isFlagEnforce) passFlag = true;
      else if (isFlagAgreement && !isFlagEnforce)
        passFlag = item.flagLoanNew === 1;
      else if (!isFlagAgreement && isFlagEnforce)
        passFlag = item.flagLoanNew === 2;

      if (!passFlag) return false;

      // ── search filter (ใหม่) ──
      if (!keyword) return true;

      const contno = (item.newContno || "").toLowerCase();
      const name = `${item.SNAM || ""}${item.NAME1 || ""} ${item.NAME2 || ""}`
        .trim()
        .toLowerCase();

      return contno.includes(keyword) || name.includes(keyword);
    });
  }, [arrayPay, isFlagAgreement, isFlagEnforce, searchText]);

  // ── Columns ───────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 20,
      render: (_, __, i) => i + 1,
    },
    {
      title: "เลขสัญญา",
      key: "contract_model",
      align: "center",
      width: 30,
      render: (_, r) => (
        <div style={{ fontWeight: 600 }}>{r.newContno || "-"}</div>
      ),
    },
    {
      title: "กลุ่ม",
      align: "center",
      key: "flagLoanNew",
      dataIndex: "flagLoanNew",
      width: 20,
      render: (_, r) => (
        <div style={{ fontWeight: 600 }}>
          {r.flagLoanNew === 1
            ? "ทำยอม"
            : r.flagLoanNew === 2
              ? "พิพากษา"
              : "-"}
        </div>
      ),
    },
    {
      title: "ชื่อลูกค้า",
      key: "name",
      align: "left",
      width: 140,
      render: (_, r) => (
        <div>
          {`${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim() || "-"}
        </div>
      ),
    },
    {
      title: "วันที่ทำสัญญา",
      align: "center",
      width: 90,
      render: (_, r) => (
        <div>
          {r.SDATE ? dayjs(r.SDATE).add(543, "year").format("DD/MM/YYYY") : "-"}
        </div>
      ),
    },
    {
      title: "งวด",
      key: "T_NOPAY",
      dataIndex: "T_NOPAY",
      align: "center",
      render: (_, r) => <>{r.flagLoanNew === 1 ? r.T_NOPAY : 1} งวด</>,
    },
    {
      title: "ยอดผ่อนชำระ",
      key: "TOT_UPAY",
      align: "right",
      width: 140,
      render: (_, r) => (
        <>{currencyFormatPoint(getRowAmounts(r).yodPhonChamra)}</>
      ),
    },
    {
      title: "ยอดค่างวด",
      key: "TOT_UPAY",
      align: "right",
      width: 140,
      render: (_, r) => <>{currencyFormatPoint(getRowAmounts(r).yodKangwad)}</>,
    },
    {
      title: "ยอดชำระแล้ว",
      key: "SMPAY",
      align: "right",
      width: 140,
      render: (_, r) => (
        <div>{currencyFormatPoint(getRowAmounts(r).yodChamraLaew)}</div>
      ),
    },
    {
      title: "ค้างชำระ",
      align: "right",
      width: 140,
      render: (_, r) => (
        <div>{currencyFormatPoint(getRowAmounts(r).kangChamra)}</div>
      ),
    },
    {
      title: "งวดเหลือ",
      key: "T_NOPAY_KONG",
      dataIndex: "T_NOPAY_KONG",
      align: "center",
      render: (_, r) => <>{r.T_NOPAY_KONG} งวด</>,
    },
    {
      title: "ลูกหนี้คงเหลือ",
      align: "right",
      width: 140,
      render: (_, r) => (
        <div>{currencyFormatPoint(getRowAmounts(r).lukniKongluea)}</div>
      ),
    },
  ];

  // ── Export Excel ──────────────────────────────────────
  const handleExportExcel = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const fromDate = startDate ? dayjs(startDate).format("DD/MM/YYYY") : "";
    const toDate = endDate ? dayjs(endDate).format("DD/MM/YYYY") : "";

    const rows = filteredData.map((r, i) => {
      const v = getRowAmounts(r);

      return {
        ลำดับ: i + 1,
        เลขสัญญา: r.CONTNO || "",
        เลขสัญญาใหม่: r.newContno || "",
        กลุ่ม: r.GCODE || "",
        สถานะ:
          r.flagLoanNew === 1 && r.LAW_TYPE_ID !== 6
            ? "ทำยอม"
            : r.flagLoanNew === 1 && r.LAW_TYPE_ID === 6
              ? "ทำยอม(ฟ้องส่วนต่าง)"
              : r.flagLoanNew === 2 && r.LAW_TYPE_ID === 6
                ? "พิพากษา(ฟ้องส่วนต่าง)"
                : "พิพากษา",
        ชื่อลูกค้า: `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim(),
        เลขบัตร: r.CUSCOD || "",
        เลขตัวถัง: r.STRNO || "",
        ยี่ห้อ: r.TYPE || "",
        รุ่น: r.MODEL || "",
        ปีผลิต: r.MANUYR || "",
        ทะเบียน: r.REGNO || "",
        เลขเครื่อง: r.ENGNO || "",
        วันทำสัญญา: r.SDATE
          ? dayjs(r.SDATE).add(543, "year").format("DD/MM/YYYY")
          : "",
        งวดแรก: r.FDATE
          ? dayjs(r.FDATE).add(543, "year").format("DD/MM/YYYY")
          : "",
        งวดสุดท้าย: r.LDATE
          ? dayjs(r.LDATE).add(543, "year").format("DD/MM/YYYY")
          : "-",
        จำนวนงวด: r.flagLoanNew === 1 ? (r.T_NOPAY ?? 1) : 1,
        ยอดผ่อนชำระ: v.yodPhonChamra,
        ยอดค่างวด: v.yodKangwad,
        ยอดชำระแล้ว: v.yodChamraLaew,
        ค้างชำระ: v.kangChamra,
        งวดเหลือ: r.T_NOPAY_KONG ?? 0,
        ลูกหนี้คงเหลือ: v.lukniKongluea,
      };
    });

    const moneyKeys = [
      "ยอดผ่อนชำระ",
      "ยอดค่างวด",
      "ยอดชำระแล้ว",
      "ค้างชำระ",
      "ลูกหนี้คงเหลือ",
    ];

    const summaryRow = {
      ลำดับ: "",
      เลขสัญญา: `รวมทั้งหมด ${filteredData.length} รายการ`,
      เลขสัญญาใหม่: "",
      กลุ่ม: "",
      สถานะ: "",
      ชื่อลูกค้า: "",
      เลขบัตร: "",
      เลขตัวถัง: "",
      ยี่ห้อ: "",
      รุ่น: "",
      ปีผลิต: "",
      ทะเบียน: "",
      เลขเครื่อง: "",
      วันทำสัญญา: "",
      งวดแรก: "",
      งวดสุดท้าย: "",
      จำนวนงวด: "",
      ยอดผ่อนชำระ: rows.reduce((s, r) => s + (r.ยอดผ่อนชำระ || 0), 0),
      ยอดค่างวด: rows.reduce((s, r) => s + (r.ยอดค่างวด || 0), 0),
      ยอดชำระแล้ว: rows.reduce((s, r) => s + (r.ยอดชำระแล้ว || 0), 0),
      ค้างชำระ: rows.reduce((s, r) => s + (r.ค้างชำระ || 0), 0),
      งวดเหลือ: "",
      ลูกหนี้คงเหลือ: rows.reduce((s, r) => s + (r.ลูกหนี้คงเหลือ || 0), 0),
    };

    const allRows = [...rows, summaryRow];
    const ws = XLSX.utils.json_to_sheet(allRows);
    const keys = Object.keys(rows[0]);

    const evenFill = { patternType: "solid", fgColor: { rgb: "EBF5FF" } };
    const oddFill = { patternType: "solid", fgColor: { rgb: "FFFFFF" } };
    const summaryFill = { patternType: "solid", fgColor: { rgb: "D5E8D4" } };
    const borderStyle = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    // header style
    keys.forEach((_, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "344962" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderStyle,
      };
    });

    // data rows style
    rows.forEach((row, ri) => {
      const fill = ri % 2 === 0 ? evenFill : oddFill;
      keys.forEach((key, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };
        const isMoney = moneyKeys.includes(key);
        const getAlign = (k) => {
          if (k === "ชื่อลูกค้า") return "left";
          if (moneyKeys.includes(k)) return "right";
          return "center";
        };
        ws[cellRef].s = {
          fill,
          font: { sz: 11 },
          alignment: { horizontal: getAlign(key), vertical: "center" },
          border: borderStyle,
          numFmt: isMoney ? "#,##0.00" : undefined,
        };
      });
    });

    // summary row style
    const summaryRowIdx = rows.length + 1;
    keys.forEach((key, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: summaryRowIdx, c: ci });
      if (!ws[cellRef]) return;
      const isMoney = moneyKeys.includes(key);
      ws[cellRef].s = {
        fill: summaryFill,
        font: { bold: true, sz: 12 },
        alignment: {
          horizontal: isMoney ? "right" : "left",
          vertical: "center",
        },
        border: borderStyle,
        numFmt: isMoney ? "#,##0.00" : undefined,
      };
    });

    ws["!cols"] = keys.map((key) => ({ wch: Math.max(key.length * 2.2, 14) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(
      wb,
      `รายงานลูกหนี้คงเหลือ${
        isFlagAgreement && isFlagEnforce
          ? ""
          : !isFlagAgreement && isFlagEnforce
            ? "(พิพากษา)"
            : "(ทำยอม)"
      }_${fromDate}_${toDate}.xlsx`,
    );
    message.success("Export Excel สำเร็จ");
  };

  // ── Export PDF ────────────────────────────────────────
  const handlePrintPDF = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");
    const fromDate = startDate ? dayjs(startDate).format("DD/MM/YYYY") : "";
    const toDate = endDate ? dayjs(endDate).format("DD/MM/YYYY") : "";

    const drawHeader = (pageNum) => {
      doc.setFont("THSarabunNew", "bold");
      doc.setFontSize(16);
      doc.text("รายงานลูกหนี้คงเหลือ", pageWidth / 2, 8, { align: "center" });
      doc.text(
        `ขายจากวันที่  ${fromDate}   ถึงวันที่  ${toDate}`,
        pageWidth / 2,
        14,
        { align: "center" },
      );
      doc.setFont("THSarabunNew", "normal");
      doc.setFontSize(14);
      doc.text("บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)", 8, 18);
      doc.text(
        "89 หมู่ 14 ถ.มิตรภาพ ต.ในเมือง อ.เมืองขอนแก่น จ.ขอนแก่น 40000",
        8,
        22,
      );
      doc.text(`วันที่พิมพ์ ${now}`, 8, 28);
      doc.text(`หน้าที่ ${pageNum}`, pageWidth - 8, 28, { align: "right" });
    };

    const tableBody = filteredData.map((r, i) => {
      const v = getRowAmounts(r);

      return [
        i + 1,
        r.newContno || "-",
        r.GCODE || "-",
        `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim() || "-",
        r.SDATE ? dayjs(r.SDATE).add(543, "year").format("DD/MM/YYYY") : "-",
        `${r.flagLoanNew === 1 ? r.T_NOPAY : 1} งวด`,
        currencyFormatPoint(v.yodPhonChamra),
        currencyFormatPoint(v.yodKangwad),
        currencyFormatPoint(v.yodChamraLaew),
        currencyFormatPoint(v.kangChamra),
        `${r.T_NOPAY_KONG} งวด`,
        currencyFormatPoint(v.lukniKongluea),
      ];
    });

    const t = calcSummary(filteredData);

    const cs = {
      fontStyle: "bold",
      font: "THSarabunNew",
      fillColor: [236, 240, 241],
    };
    const cr = { ...cs, halign: "right" };

    const summaryRow = [
      {
        content: `รวมทั้งหมด  ${filteredData.length} รายการ`,
        colSpan: 6,
        styles: cs,
      },
      { content: currencyFormatPoint(t.yodPhonChamra), styles: cr },
      { content: currencyFormatPoint(t.yodKangwad), styles: cr },
      { content: currencyFormatPoint(t.yodChamraLaew), styles: cr },
      { content: currencyFormatPoint(t.kangChamra), styles: cr },
      { content: "-", styles: cs },
      { content: currencyFormatPoint(t.lukniKongluea), styles: cr },
    ];

    doc.autoTable({
      startY: 30,
      head: [
        [
          "ลำดับ",
          "เลขสัญญา",
          "กลุ่ม",
          "ชื่อลูกค้า",
          "วันที่ทำสัญญา",
          "งวด",
          "ยอดผ่อนชำระ",
          "ยอดค่างวด",
          "ยอดชำระแล้ว",
          "ค้างชำระ",
          "งวดเหลือ",
          "ลูกหนี้คงเหลือ",
        ],
      ],
      body: [...tableBody, summaryRow],
      styles: {
        font: "THSarabunNew",
        fontSize: 13,
        cellPadding: 1,
        halign: "center",
        fillColor: [255, 255, 255],
      },
      headStyles: {
        font: "THSarabunNew",
        fillColor: [52, 73, 94],
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 13,
      },
      columnStyles: {
        3: { halign: "left" }, // ชื่อลูกค้า
        6: { halign: "right" }, // ยอดผ่อนชำระ
        7: { halign: "right" }, // ยอดค่างวด
        8: { halign: "right" }, // ยอดชำระแล้ว
        9: { halign: "right" }, // ค้างชำระ
        11: { halign: "right" }, // ลูกหนี้คงเหลือ
      },
      alternateRowStyles: { fillColor: [235, 245, 255] },
      tableWidth: "auto",
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
      margin: { top: 30, left: 3, right: 3 },
    });

    doc.save(
      `รายงานลูกหนี้คงเหลือ${
        isFlagAgreement && isFlagEnforce
          ? ""
          : !isFlagAgreement && isFlagEnforce
            ? "(พิพากษา)"
            : "(ทำยอม)"
      }_${fromDate}_${toDate}.pdf`,
    );
  };

  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  const hasDate = startDate && endDate;
  const filteredData = useMemo(() => handleFilterData(), [handleFilterData]);
  console.log("filteredData", filteredData);

  return (
    <>
      <style>{`
        .row-even td { background-color: #f5f8ff !important; }
        .row-odd  td { background-color: #ffffff !important; }
      `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Row>
          <Col span={20}>
            <Space style={{ marginBottom: 10 }} wrap>
              เริ่ม
              <ThaiDatePicker
                size="large"
                value={startDate}
                onChange={setStartDate}
                placeholder={"วันที่เริ่มต้น"}
                allowClear={false}
              />
              ถึง
              <ThaiDatePicker
                size="large"
                value={endDate}
                onChange={setEndDate}
                placeholder={"วันที่สิ้นสุด"}
                allowClear={false}
              />
              <Button size="large" type="primary" onClick={onQuery}>
                ค้นหา
              </Button>
              <Checkbox
                checked={isFlagAgreement}
                onChange={(e) => setIsFlagAgreement(e.target.checked)}
              >
                ทำยอม
              </Checkbox>
              <Checkbox
                checked={isFlagEnforce}
                onChange={(e) => setIsFlagEnforce(e.target.checked)}
              >
                พิพากษา
              </Checkbox>
              {/* ✅ ช่องค้นหาในตาราง */}
              <Input
                size="small"
                allowClear
                placeholder="ค้นหาเลขสัญญา / ชื่อลูกค้า"
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 260 }}
              />
            </Space>
          </Col>
          <Col span={4} style={{ textAlign: "end" }}>
            <Space style={{ marginBottom: 10 }}>
              <Dropdown menu={exportMenuItems} disabled={!filteredData.length}>
                <Button size="large" icon={<PrinterOutlined />}>
                  พิมพ์ / Export <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>

        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>รายงานลูกหนี้คงเหลือ</Divider>
          <Typography.Text
            strong
            style={{ display: "block", textAlign: "center", fontSize: 16 }}
          >
            บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)
          </Typography.Text>
          {hasDate && (
            <Typography.Text
              style={{ display: "block", textAlign: "center", fontSize: 16 }}
            >
              ขายจากวันที่ {dayjs(startDate).format("DD/MM/YYYY")} ถึงวันที่{" "}
              {dayjs(endDate).format("DD/MM/YYYY")}
            </Typography.Text>
          )}

          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={(r) => r.idJudge ?? r.CONTNO}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "row-even" : "row-odd"
            }
            scroll={{ x: 1400 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            summary={() => {
              const t = calcSummary(filteredData);
              const cs = { fontWeight: "bold", background: "#ecf0f1" };
              const cr = {
                fontWeight: "bold",
                background: "#ecf0f1",
                textAlign: "right",
              };

              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell colSpan={6} align="left" style={cs}>
                    รวมทั้งหมด {filteredData.length} รายการ
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.yodPhonChamra)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.yodKangwad)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.yodChamraLaew)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.kangChamra)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell style={cs} />
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.lukniKongluea)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </Card>
      </Spin>
    </>
  );
};

const MainReportDebtors = MotionHoc(Main);
export default MainReportDebtors;
